import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  CheckCircle2,
  FileText,
  RefreshCw,
  RotateCcw,
  Save,
  XCircle,
} from "lucide-react";
import { toast } from "react-toastify";
import { apiResource, containerOrderIntakeAPI } from "../../services/api";
import { useAppDispatch, useAppSelector } from "../../hooks/useStore";
import { getCountries } from "../../store/slices/companySlice";
import Button from "../../components/UI/Button";
import SectionCard from "../../components/UI/SectionCard";
import LoadingSpinner from "../../components/UI/LoadingSpinner";
import { Modal } from "../../components/UI/Modal";
import { FormField, Input, Select, Textarea } from "../../components/UI/FormField";
import IntakeField from "../../components/containerOrderIntake/IntakeField";
import HandlerAssign from "../../components/handler/HandlerAssign";
import IntakeNote from "../../components/containerOrderIntake/IntakeNote";
import IntakeSourcePanel from "../../components/containerOrderIntake/IntakeSourcePanel";
import IntakeStopCard from "../../components/containerOrderIntake/IntakeStopCard";
import IntakeContainerCard from "../../components/containerOrderIntake/IntakeContainerCard";
import type { IntakeLinkAction } from "../../components/containerOrderIntake/IntakeLinkPanel";
import { toSnapshot } from "../../components/directory/DirectorySelect";
import { CONTAINER_TYPE_OPTIONS } from "../../constants/containerOrderConstants";
import { INCOTERM_OPTIONS } from "../../constants/taskConstants";
import { GOOGLE_PLACES_API_KEY } from "../../constants/shipmentFormConstants";
import { useSocketEvent } from "../../hooks/useSocketEvent";
import { INTAKE_CHANGED_EVENT } from "../../hooks/useIntakeCounts";
import type { EntitySnapshot } from "../../types";
import type {
  ContainerOrderIntake,
  IntakeDraft,
  IntakeIssue,
  IntakeLinkKey,
} from "../../types/containerOrderIntake";
import { getIn, linkBlockPath, linkIdField, setIn } from "../../utils/intakeDraft";

const fmtDateTime = (d?: string | null) =>
  d ? new Date(d).toLocaleString("et-EE", { dateStyle: "short", timeStyle: "short" }) : "—";

const errorOf = (e: any, fallback: string) => e?.response?.data?.message || fallback;

/**
 * One Oliver order under review.
 *
 * Left, what the document said. Right, the container order it is about to
 * become — every field editable, every company tied to a directory record or
 * marked to be created. Accept is one transaction on the server: the new
 * directory records, the order and all its containers, or nothing.
 */
const ContainerOrderIntakeReview: React.FC = () => {
  const { t } = useTranslation("containerOrderIntake");
  const { intakeId = "" } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { countries } = useAppSelector((state) => state.company);
  const countryList = useMemo(() => (countries as any[])?.flat?.() ?? [], [countries]);

  const [intake, setIntake] = useState<ContainerOrderIntake | null>(null);
  const [draft, setDraft] = useState<IntakeDraft | null>(null);
  const [issues, setIssues] = useState<IntakeIssue[]>([]);
  const [truckLabels, setTruckLabels] = useState<Record<string, string>>({});
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<null | "save" | "accept" | "decline" | "restore" | "rematch">(null);
  const [declineOpen, setDeclineOpen] = useState(false);
  const [declineReason, setDeclineReason] = useState("");
  const [staleByOthers, setStaleByOthers] = useState(false);

  const readOnly = intake?.status !== "pending";

  const adopt = useCallback((next: ContainerOrderIntake) => {
    setIntake(next);
    setDraft(next.draft);
    setIssues(next.issues || []);
    setTruckLabels(next.vehicles || {});
    setDirty(false);
    setStaleByOthers(false);
  }, []);

  const load = useCallback(async () => {
    try {
      const res = await containerOrderIntakeAPI.get(intakeId);
      adopt(res.data.data.intake);
    } catch (e: any) {
      toast.error(errorOf(e, t("review.loadFailed")));
    } finally {
      setLoading(false);
    }
  }, [intakeId, adopt, t]);

  useEffect(() => {
    load();
    if (!countryList.length) dispatch(getCountries());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intakeId]);

  // Nothing on this page reaches the server until Save, so a tab closed with
  // edits on it loses them.
  useEffect(() => {
    if (!dirty) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const leave = useCallback(() => {
    if (dirty && !window.confirm(t("review.leaveUnsaved"))) return;
    navigate("/order-intake");
  }, [dirty, navigate, t]);

  // Somebody else saved or decided this order while it was open.
  useSocketEvent<{ intakeId?: string; updatedAt?: string | null }>(INTAKE_CHANGED_EVENT, (change) => {
    if (change?.intakeId !== intakeId || busy) return;
    // The echo of this tab's own save.
    if (change.updatedAt && intake?.updatedAt && new Date(change.updatedAt).getTime() === new Date(intake.updatedAt).getTime()) return;
    if (dirty) setStaleByOthers(true);
    else load();
  });

  // ── Editing ──────────────────────────────────────────────────────────────
  const update = useCallback((path: string, value: unknown) => {
    setDraft((d) => (d ? setIn(d, path, value) : d));
    setDirty(true);
  }, []);

  const updateMany = useCallback((changes: Record<string, unknown>) => {
    setDraft((d) =>
      d ? Object.entries(changes).reduce((acc, [path, value]) => setIn(acc, path, value), d) : d,
    );
    setDirty(true);
  }, []);

  const persist = useCallback(
    async (next?: IntakeDraft) => {
      if (!intake) return null;
      setBusy("save");
      try {
        const res = await containerOrderIntakeAPI.save(intake._id, next ?? draft, intake.updatedAt);
        const saved: ContainerOrderIntake = res.data.data.intake;
        adopt(saved);
        return saved;
      } catch (e: any) {
        if (e?.response?.status === 409) setStaleByOthers(true);
        toast.error(errorOf(e, t("review.saveFailed")));
        return null;
      } finally {
        setBusy(null);
      }
    },
    [intake, draft, adopt, t],
  );

  /** Fill the client or a stop from the directory record it now points at. */
  const applySnapshot = useCallback(
    (draftIn: IntakeDraft, key: IntakeLinkKey, base: string, snapshot: EntitySnapshot) => {
      const idField = linkIdField(key);
      const country =
        typeof snapshot.address.country === "object" && snapshot.address.country
          ? (snapshot.address.country as any)._id
          : (snapshot.address.country as string) || null;

      let next = setIn(draftIn, `${base}.${idField}`, snapshot._id ?? null);
      next = setIn(next, `${base}.companyName`, snapshot.companyName);
      next = setIn(next, `${base}.address`, {
        ...getIn(next, `${base}.address`),
        street: snapshot.address.street || "",
        city: snapshot.address.city || "",
        state: snapshot.address.state || "",
        postalCode: snapshot.address.postalCode || "",
        country,
        coordinates: snapshot.address.coordinates ?? getIn(next, `${base}.address.coordinates`) ?? {
          latitude: null,
          longitude: null,
        },
      });
      if (key === "client") {
        next = setIn(next, `${base}.registerationCode`, snapshot.registerationCode || "");
        if (snapshot.emailAddress) next = setIn(next, `${base}.emailAddress`, snapshot.emailAddress);
        if (snapshot.phoneNumber) next = setIn(next, `${base}.phoneNumber`, snapshot.phoneNumber);
      }
      if (key === "loadingSite") {
        // As on the order form: the site contact comes off the picked address
        // and stays editable.
        next = setIn(next, `${base}.contactPhone`, snapshot.phoneNumber || "");
        next = setIn(next, `${base}.contactEmail`, snapshot.emailAddress || "");
      }
      return next;
    },
    [],
  );

  /**
   * A change to a directory link, like every other edit: held on the draft
   * until Save.
   *
   * Confirming one of the matcher's candidates reads that record first — a
   * candidate carries only enough to recognise it (name, code, town), and the
   * stop has to show the address the order will actually be driven to rather
   * than a half-filled one. Save re-reads it server-side and re-runs the
   * checks, so the directory still has the last word.
   */
  const changeLink = useCallback(
    async (key: IntakeLinkKey, action: IntakeLinkAction) => {
      if (!draft) return;
      const base = linkBlockPath(draft, key);
      if (!base) return;
      const idField = linkIdField(key);
      let next = draft;

      const setLink = (status: string) =>
        setIn(setIn(next, `links.${key}.status`, status), `links.${key}.decided`, true);

      if (action.type === "candidate") {
        const directory = draft.links?.[key]?.directory;
        try {
          const res = await apiResource(directory as any).getOne(action.id);
          const record = res.data.data.partner ?? res.data.data.address;
          next = applySnapshot(next, key, base, toSnapshot(record));
        } catch {
          // The record would not load; link the id and let Save fill the rest.
          next = setIn(next, `${base}.${idField}`, action.id);
        }
        next = setLink("linked");
      } else if (action.type === "pick") {
        next = applySnapshot(next, key, base, action.snapshot);
        next = setLink("linked");
      } else {
        next = setIn(next, `${base}.${idField}`, null);
        next = setLink("missing");
      }

      setDraft(next);
      setDirty(true);
    },
    [draft, applySnapshot],
  );

  const onTruck = useCallback(
    (index: number, id: string | null, label: string) => {
      update(`lines.${index}.vehicleId`, id);
      if (id) setTruckLabels((labels) => ({ ...labels, [id]: label }));
    },
    [update],
  );

  // ── Decisions ────────────────────────────────────────────────────────────
  const handleAccept = async () => {
    if (!intake) return;
    let current = intake;
    if (dirty) {
      const saved = await persist();
      if (!saved) return;
      current = saved;
      if ((saved.issues || []).some((i) => i.severity === "blocking")) {
        toast.error(t("review.stillBlocked"));
        return;
      }
    }
    setBusy("accept");
    try {
      const res = await containerOrderIntakeAPI.accept(current._id, current.updatedAt);
      toast.success(res.data.message);
      navigate(`/container-orders/${res.data.data.order._id}`);
    } catch (e: any) {
      const serverIssues = e?.response?.data?.issues;
      if (Array.isArray(serverIssues)) setIssues(serverIssues);
      if (e?.response?.status === 409) setStaleByOthers(true);
      toast.error(errorOf(e, t("review.acceptFailed")));
    } finally {
      setBusy(null);
    }
  };

  const simple = async (
    kind: "decline" | "restore" | "rematch",
    call: () => Promise<any>,
  ) => {
    setBusy(kind);
    try {
      const res = await call();
      toast.success(res.data.message);
      await load();
      return true;
    } catch (e: any) {
      toast.error(errorOf(e, t("review.actionFailed")));
      return false;
    } finally {
      setBusy(null);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner size="md" />
      </div>
    );
  }
  if (!intake) {
    return (
      <div className="p-6 text-[13px] text-[#8A9590]">
        {t("review.notFound")} <Link to="/order-intake" className="text-[#C97A3C]">{t("review.back")}</Link>
      </div>
    );
  }

  const blocking = issues.filter((i) => i.severity === "blocking").length;
  const order = draft?.order;

  const general = (path: string, label: string, opts: { type?: string; required?: boolean } = {}) => (
    <IntakeField path={path} label={label} issues={issues} required={opts.required}>
      {(error) => (
        <Input
          size="sm"
          type={opts.type || "text"}
          value={getIn(draft, path) ?? ""}
          error={error}
          disabled={readOnly}
          onChange={(e) =>
            update(path, opts.type === "date" ? e.target.value || null : e.target.value)
          }
        />
      )}
    </IntakeField>
  );

  const stopProps = {
    draft: draft!,
    issues,
    countries: countryList,
    apiKey: GOOGLE_PLACES_API_KEY,
    readOnly,
    onChange: update,
    onChangeMany: updateMany,
    onLink: changeLink,
  };

  return (
    <div className="p-4 md:p-6 space-y-4">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <button
            onClick={leave}
            className="mt-1 p-1 rounded hover:bg-[#F5F4F0]"
            aria-label={t("review.back")}
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-xl font-medium text-[#0B0F0E]">
              {t("review.title", { id: intake.externalId })}
            </h1>
            <div className="flex flex-wrap items-center gap-x-3 text-[13px] text-[#8A9590]">
              {/* Shown only when there is one — see the list's Document column. */}
              {intake.sourceFileName && (
                <span className="inline-flex items-center gap-1">
                  <FileText className="h-3.5 w-3.5" />
                  {intake.sourceFileName}
                </span>
              )}
              <span>{t("review.received", { when: fmtDateTime(intake.receivedAt || intake.createdAt) })}</span>
              <span className="rounded-full bg-[#F5F4F0] px-2 py-0.5 text-[11px] text-[#4A5550]">
                {t(`status.${intake.status}`)}
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* ── Who is reviewing this ──────────────────────────────────────
              In the header, with the record's own facts — not in General
              information below, which is the *order being built* and every
              field of which is a draft value the assessment validates. The
              handler is neither: it is a note about who at this office is
              dealing with the order, it is saved the instant it is pressed
              rather than with the draft, and it deliberately cannot block
              Accept the way a draft field can.

              Pressable in every status. A declined order can be restored and an
              accepted one is the provenance of a live booking somebody is
              answering for, so both still belong to somebody. */}
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-[#8A9590]">
              {t("review.handler")}
            </span>
            <HandlerAssign
              handler={intake.handlerId ?? null}
              label={t("review.handler")}
              onAssign={async (handlerId) => {
                const res = await containerOrderIntakeAPI.setHandler(
                  intake._id,
                  handlerId,
                );
                /**
                 * Fold the answer in rather than reloading.
                 *
                 * `load()` would discard whatever the reviewer has typed into
                 * the draft since — this page holds unsaved edits and warns
                 * about them (`dirty`) — and claiming an order must not be a
                 * way of losing twenty minutes of review.
                 */
                setIntake((prev) =>
                  prev
                    ? { ...prev, handlerId: res.data?.data?.handler ?? null }
                    : prev,
                );
                return res;
              }}
              onError={(message) => toast.error(message)}
            />
          </div>

          {intake.status === "pending" && (
            <Button
              variant="ghost"
              size="sm"
              icon={<RefreshCw className="h-4 w-4" />}
              onClick={() => simple("rematch", () => containerOrderIntakeAPI.rematch(intake._id))}
              isLoading={busy === "rematch"}
              disabled={dirty}
              title={t("review.rematchHint")}
            >
              {t("review.rematch")}
            </Button>
          )}
        </div>
      </div>

      {/* State banners */}
      {staleByOthers && (
        <div className="flex items-center justify-between gap-2 bg-amber-50 border border-amber-200 text-amber-900 text-[13px] rounded-md px-3 py-2">
          {t("review.changedElsewhere")}
          <Button variant="outline" size="sm" onClick={load}>
            {t("review.reload")}
          </Button>
        </div>
      )}
      {intake.status === "accepted" && (
        <div className="flex items-center gap-2 bg-green-50 border border-green-200 text-green-900 text-[13px] rounded-md px-3 py-2">
          <CheckCircle2 className="h-4 w-4" />
          {t("review.acceptedBanner", { when: fmtDateTime(intake.decidedAt) })}
          {intake.containerOrderId && (
            <Link
              to={`/container-orders/${intake.containerOrderId._id}`}
              className="font-medium text-[#C97A3C] hover:underline"
            >
              {intake.containerOrderId.orderNumber}
            </Link>
          )}
        </div>
      )}
      {intake.status === "declined" && (
        <div className="flex flex-wrap items-center justify-between gap-2 bg-gray-50 border border-gray-200 text-[13px] rounded-md px-3 py-2">
          <span>
            {t("review.declinedBanner", { when: fmtDateTime(intake.decidedAt) })}{" "}
            <em>{intake.declineReason}</em>
          </span>
          <Button
            variant="outline"
            size="sm"
            icon={<RotateCcw className="h-4 w-4" />}
            isLoading={busy === "restore"}
            onClick={() => simple("restore", () => containerOrderIntakeAPI.restore(intake._id))}
          >
            {t("review.restore")}
          </Button>
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(280px,360px)_1fr] gap-4 items-start">
        {/* What Oliver sent */}
        <div className="lg:sticky lg:top-4 lg:max-h-[calc(100dvh-11rem)] lg:overflow-y-auto bg-white border border-[#E5E3DC] rounded-lg p-3">
          <div className="text-[13px] font-medium text-[#0B0F0E] mb-1">{t("source.title")}</div>
          <IntakeSourcePanel raw={intake.raw} />
        </div>

        {/* The order it becomes */}
        {draft && order ? (
          <div className="space-y-4 min-w-0">
            <SectionCard title={t("review.sections.general")}>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {general("order.referenceNumber", t("review.fields.reference"))}
                {general("order.contractNumber", t("review.fields.booking"))}
                <IntakeField
                  path="order.containerType"
                  label={t("review.fields.containerType")}
                  issues={issues}
                  required
                  readOnly={readOnly}
                  onApplySuggestion={(field, value) => update(field, value)}
                >
                  {(error) => (
                    <Select
                      size="sm"
                      value={order.containerType ?? ""}
                      error={error}
                      disabled={readOnly}
                      placeholder={t("review.fields.chooseType")}
                      options={CONTAINER_TYPE_OPTIONS}
                      onChange={(e) => update("order.containerType", e.target.value)}
                    />
                  )}
                </IntakeField>
                <IntakeField path="order.incoterm" label={t("review.fields.incoterm")} issues={issues}>
                  {(error) => (
                    <Select
                      size="sm"
                      value={order.incoterm ?? ""}
                      error={error}
                      disabled={readOnly}
                      placeholder="—"
                      options={INCOTERM_OPTIONS.map((code) => ({ value: code, label: code }))}
                      onChange={(e) => update("order.incoterm", e.target.value)}
                    />
                  )}
                </IntakeField>
                {general("order.place", t("review.fields.place"))}
                <div className="grid grid-cols-2 gap-3">
                  {general("order.dateFrom", t("review.fields.dateFrom"), { type: "date" })}
                  {general("order.dateTo", t("review.fields.dateTo"), { type: "date" })}
                </div>
              </div>
              <div className="mt-3">
                <IntakeField path="order.additionalNotes" label={t("review.fields.notes")} issues={issues}>
                  {() => (
                    <IntakeNote
                      value={order.additionalNotes ?? ""}
                      disabled={readOnly}
                      onChange={(e) => update("order.additionalNotes", e.target.value)}
                    />
                  )}
                </IntakeField>
              </div>
            </SectionCard>

            <IntakeStopCard
              {...stopProps}
              linkKey="client"
              title={t("review.sections.client")}
              sourceLabel={t("review.from.orderingParty")}
            />

            <div className="space-y-3">
              <div className="text-[13px] font-medium text-[#0B0F0E]">{t("review.sections.route")}</div>
              <IntakeStopCard
                {...stopProps}
                linkKey="terminalPickup"
                title={t("review.stops.terminalPickup")}
                sourceLabel={t("review.from.containerPickup")}
              />
              <IntakeStopCard
                {...stopProps}
                linkKey="loadingSite"
                title={t("review.stops.loadingSite")}
                sourceLabel={t("review.from.loadingLocation")}
              />
              {/* Only a draft stored before the drop-off terminal took over the
                  unloading address still has a stop of its own here. */}
              {order.route.additionalStops.some((stop) => stop.intakeRole === "unloading") && (
                <IntakeStopCard
                  {...stopProps}
                  linkKey="unloading"
                  title={t("review.stops.unloading")}
                  sourceLabel={t("review.from.unLoadingLocation")}
                />
              )}
              <IntakeStopCard
                {...stopProps}
                linkKey="terminalDropoff"
                title={t("review.stops.terminalDropoff")}
                sourceLabel={t("review.from.dropOff")}
              />
            </div>

            <div className="space-y-3">
              <div className="text-[13px] font-medium text-[#0B0F0E]">
                {t("review.sections.containers", { count: draft.lines.length })}
              </div>
              {draft.lines.map((line, index) => (
                <IntakeContainerCard
                  key={index}
                  index={index}
                  line={line}
                  orderType={order.containerType}
                  source={intake.raw?.containers?.[index]}
                  truckLabel={line.vehicleId ? truckLabels[line.vehicleId] ?? "" : ""}
                  issues={issues}
                  readOnly={readOnly}
                  onChange={update}
                  onTruck={onTruck}
                />
              ))}
            </div>
          </div>
        ) : (
          <div className="text-[13px] text-[#8A9590]">{t("review.noDraft")}</div>
        )}
      </div>

      {/* Actions. Sticky inside the page rather than fixed to the window: a
          window-fixed bar spans the whole screen — over the sidebar and its
          Help Centre footer — while this one ends with the content column. */}
      {intake.status === "pending" && draft && (
        <div className="sticky bottom-0 z-20 -mx-4 md:-mx-6 -mb-4 md:-mb-6 border-t border-[#E5E3DC] bg-white/95 backdrop-blur px-4 md:px-6 py-3">
          <div className="flex flex-wrap items-center justify-end gap-2">
            <span className="mr-auto text-[13px] text-[#8A9590]">
              {dirty
                ? t("review.unsaved")
                : blocking
                  ? t("review.blockedCount", { count: blocking })
                  : t("review.readyToAccept")}
            </span>
            <Button
              variant="outline"
              size="sm"
              icon={<XCircle className="h-4 w-4" />}
              onClick={() => setDeclineOpen(true)}
              disabled={!!busy}
            >
              {t("review.decline")}
            </Button>
            <Button
              variant="outline"
              size="sm"
              icon={<Save className="h-4 w-4" />}
              onClick={() => persist()}
              isLoading={busy === "save"}
              disabled={!dirty || !!busy}
            >
              {t("review.save")}
            </Button>
            <Button
              variant="primary"
              size="sm"
              icon={<CheckCircle2 className="h-4 w-4" />}
              onClick={handleAccept}
              isLoading={busy === "accept"}
              disabled={!!busy || (!dirty && blocking > 0)}
            >
              {t("review.accept", { count: draft.lines.length })}
            </Button>
          </div>
        </div>
      )}

      {/* Body and footer as every other modal in the app has them: the content
          padded off the edges, the decision on its own strip under a rule. The
          Modal itself is a flex column and supplies neither. */}
      <Modal
        isOpen={declineOpen}
        onClose={() => setDeclineOpen(false)}
        title={t("review.declineTitle")}
        size="md"
      >
        <div className="flex-1 overflow-y-auto px-4 py-3 sm:p-5 space-y-4">
          <p className="text-[13px] text-[#4A5550]">{t("review.declineBody")}</p>
          <FormField
            label={t("review.declineReason")}
            required
            size="sm"
            hint={t("review.declineReasonHint")}
          >
            <Textarea
              rows={3}
              autoGrow
              maxRows={8}
              autoFocus
              value={declineReason}
              placeholder={t("review.declineReasonPlaceholder")}
              onChange={(e) => setDeclineReason(e.target.value)}
            />
          </FormField>
        </div>
        <div className="flex items-center justify-end gap-2 px-4 py-2.5 sm:px-5 sm:py-3 border-t border-[rgba(11,15,14,0.08)] bg-white shrink-0">
          <Button variant="outline" size="sm" onClick={() => setDeclineOpen(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="danger"
            size="sm"
            isLoading={busy === "decline"}
            disabled={declineReason.trim().length < 3}
            onClick={async () => {
              const ok = await simple("decline", () =>
                containerOrderIntakeAPI.decline(intake._id, declineReason.trim()),
              );
              if (ok) {
                setDeclineOpen(false);
                setDeclineReason("");
              }
            }}
          >
            {t("review.decline")}
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default ContainerOrderIntakeReview;
